/**
 * useTreatmentUnlocks — usage-earned Djay treatment finishes (mascot domain SSOT).
 *
 * Treatments are the cross-cutting "rarity finish" cosmetic axis (aura / sparkle
 * / glow / gummy) that layer on top of any variant. They are earned through
 * LIGHT learning-hub usage: the cumulative `labSessionsCompleted` counter.
 *
 * Mirrors the STANDALONE_COSMETIC_GATE_DEFS pattern in `useDjayCustomization.ts`:
 * a single TREATMENT_GATE_DEFS source feeds BOTH the in-hook progress view and
 * the exported TREATMENT_GATES persistence predicate, so a threshold lives in
 * exactly one place and the UI progress bar can never drift from the
 * persistence gate (ARCHITECTURAL_PEER_REVIEW_PLAN §Deel IV — no duplicated
 * magic numbers across UI + persistence).
 *
 * Display name + rarity come from TREATMENT_META (avatar SSOT), so a new finish
 * lights up everywhere from one source.
 */

import { useMemo } from 'react'
import { DjayTreatment, TREATMENT_META } from './DjayAvatar'
import type { VariantProgress, MascotRarity } from './useVariantUnlocks'
import type { AchievementCheckData } from './achievements/achievementData'

export interface TreatmentUnlockInfo {
  id: DjayTreatment
  name: string
  rarity: MascotRarity
  unlocked: boolean
  condition: string
  description: string
  progress: VariantProgress
}

/**
 * Single source of truth for the treatment finishes and their learning-hub
 * usage gates. `none` is always available; the rest unlock at increasing
 * cumulative completed lab-session counts (light, not grindy).
 */
interface TreatmentGateDef {
  id: DjayTreatment
  condition: string
  /** Required cumulative completed lab sessions (0 = always available). */
  max: number
  label: string
  stat: (d: AchievementCheckData) => number
}

const labSessions = (d: AchievementCheckData) => d.labSessionsCompleted ?? 0

const TREATMENT_GATE_DEFS: TreatmentGateDef[] = [
  {
    id: 'none',
    condition: 'Altijd beschikbaar',
    max: 0,
    label: 'Ontgrendeld',
    stat: () => 1,
  },
  {
    id: 'glow',
    condition: 'Voltooi 3 oefensessies',
    max: 3,
    label: 'sessies',
    stat: labSessions,
  },
  {
    id: 'gummy',
    condition: 'Voltooi 8 oefensessies',
    max: 8,
    label: 'sessies',
    stat: labSessions,
  },
  {
    id: 'sparkle',
    condition: 'Voltooi 15 oefensessies',
    max: 15,
    label: 'sessies',
    stat: labSessions,
  },
  {
    id: 'aura',
    condition: 'Voltooi 25 oefensessies',
    max: 25,
    label: 'sessies',
    stat: labSessions,
  },
]

/**
 * Returns all treatment unlock info based on current learner state, plus the
 * unlocked count. `unlockedCosmetics` keeps a finish "owned" once its gate has
 * been met (mirrors the standalone-accessory persistence behaviour).
 */
export function useTreatmentUnlocks(
  checkData: AchievementCheckData,
  unlockedCosmetics: string[] = [],
): { treatments: TreatmentUnlockInfo[]; unlockedCount: number } {
  return useMemo(() => {
    const treatments: TreatmentUnlockInfo[] = TREATMENT_GATE_DEFS.map(
      (gate) => {
        const meta = TREATMENT_META[gate.id]
        const current = Math.min(gate.stat(checkData), gate.max || 1)
        const gateMet = gate.max === 0 || gate.stat(checkData) >= gate.max
        const persisted = unlockedCosmetics.includes(gate.id)
        const unlocked = gateMet || persisted
        return {
          id: gate.id,
          name: meta.name,
          rarity: meta.rarity,
          unlocked,
          condition: gate.condition,
          description: meta.description,
          progress: {
            current: unlocked ? gate.max || 1 : current,
            max: gate.max || 1,
            label: unlocked ? 'Ontgrendeld' : gate.label,
          },
        }
      },
    )

    return {
      treatments,
      unlockedCount: treatments.filter((t) => t.unlocked).length,
    }
  }, [checkData, unlockedCosmetics])
}

/** Total number of treatment finishes available. */
export const TOTAL_TREATMENTS = TREATMENT_GATE_DEFS.length

/**
 * The treatment ids and their "is this gate met" predicate, derived from
 * TREATMENT_GATE_DEFS. Used by the collection page to persist a finish the
 * moment its gate is first met (via markCosmeticUnlocked). `none` is excluded
 * — it never needs persisting.
 */
export const TREATMENT_GATES: {
  id: DjayTreatment
  isMet: (d: AchievementCheckData) => boolean
}[] = TREATMENT_GATE_DEFS.filter((g) => g.max > 0).map((gate) => ({
  id: gate.id,
  isMet: (d) => gate.stat(d) >= gate.max,
}))
